/**
 * Money / SensitiveValue — Privacy-aware tutar gösterimi.
 *
 * Şub 16, 2026 — `usePrivacyStore` true ise tutarları blur'lar. Kullanıcı bir
 * tutarın üzerine tıklayarak (key prop'u verilmişse) o satırı geçici olarak
 * ifşa edebilir. Privacy mode kapandığında tüm reveal'lar otomatik temizlenir.
 *
 * Kullanım:
 *   <Money amount={1234.56} />           → €1.234,56  veya  blurlanmış
 *   <Money amount={42.5} currency="USD" /> → $42.50
 *   <Money amount={x} revealKey="row-7" /> → tıklanırsa geçici ifşa
 *   <SensitiveValue revealKey="phone-1">+32 477 ...</SensitiveValue>
 *
 * Tasarım:
 *   - CSS filter: blur(6px) + select-none — kopyalanmasın diye.
 *   - Tıklanabilir cursor yalnızca revealKey prop'u verilince aktif.
 *   - SSR güvenli (window kontrolü gerekmiyor; store hydrate olur).
 */
import React from 'react';
import { Eye } from 'lucide-react';
import { usePrivacyStore } from '../store/usePrivacyStore';

const CURRENCY_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', TRY: '₺',
};

function formatAmount(amount, currency = 'EUR', locale = 'nl-BE') {
  const sym = CURRENCY_SYMBOLS[currency] || currency || '';
  if (amount === null || amount === undefined || amount === '') return `${sym}—`;
  const num = typeof amount === 'number' ? amount : Number(String(amount).replace(',', '.'));
  if (!Number.isFinite(num)) return `${sym}—`;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${sym}${num.toFixed(2)}`;
  }
}

/**
 * <Money>
 *
 * Props:
 *   amount       — sayı veya string ("1234.56", "1234,56").
 *   currency     — "EUR" | "USD" | "GBP" | "TRY". Default EUR.
 *   locale       — Intl locale. Default "nl-BE".
 *   revealKey    — verilirse tıklanınca tek bu satır ifşa olur.
 *   className    — ekstra Tailwind sınıfları.
 *   prefix       — para sembolü öncesi (ör. "Toplam: ").
 *   suffix       — sonrası (ör. "/ay").
 *   data-testid  — yanlış kullanım: standart şekilde aktarılır.
 */
export const Money = React.memo(function Money({
  amount,
  currency = 'EUR',
  locale = 'nl-BE',
  revealKey,
  className = '',
  prefix = '',
  suffix = '',
  ...rest
}) {
  const privacyMode = usePrivacyStore((s) => s.privacyMode);
  const isRevealed = usePrivacyStore((s) =>
    revealKey ? s.revealedKeys.includes(revealKey) : false,
  );
  const revealAction = usePrivacyStore((s) => s.revealKey);

  const text = `${prefix}${formatAmount(amount, currency, locale)}${suffix}`;
  const hidden = privacyMode && !isRevealed;

  if (!hidden) {
    return <span className={className} {...rest}>{text}</span>;
  }

  const handleClick = revealKey
    ? () => revealAction(revealKey)
    : undefined;

  return (
    <span
      className={`relative inline-flex items-center select-none ${revealKey ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'} ${className}`}
      onClick={handleClick}
      title={revealKey ? 'Gizli — göstermek için tıklayın' : 'Sunum modu açık — tutarlar gizli'}
      aria-label="Gizli tutar"
      data-privacy="hidden"
      {...rest}
    >
      <span
        className="blur-[6px] tracking-wider"
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >
        {text || '€0,00'}
      </span>
      {revealKey && (
        <Eye className="absolute -right-4 top-1/2 -translate-y-1/2 h-3 w-3 opacity-60" />
      )}
    </span>
  );
});

/**
 * <SensitiveValue> — Para olmayan hassas alanlar için (IBAN, telefon, e-posta…).
 * Children'ı blur'lar; revealKey verilirse tıklanınca ifşa olur.
 */
export const SensitiveValue = React.memo(function SensitiveValue({
  children,
  revealKey,
  className = '',
  ...rest
}) {
  const privacyMode = usePrivacyStore((s) => s.privacyMode);
  const isRevealed = usePrivacyStore((s) =>
    revealKey ? s.revealedKeys.includes(revealKey) : false,
  );
  const revealAction = usePrivacyStore((s) => s.revealKey);

  const hidden = privacyMode && !isRevealed;

  if (!hidden) {
    return <span className={className} {...rest}>{children}</span>;
  }

  const handleClick = revealKey
    ? () => revealAction(revealKey)
    : undefined;

  return (
    <span
      className={`relative inline-flex items-center select-none ${revealKey ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'} ${className}`}
      onClick={handleClick}
      title={revealKey ? 'Gizli — göstermek için tıklayın' : 'Sunum modu açık'}
      data-privacy="hidden"
      {...rest}
    >
      <span
        className="blur-[5px]"
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >
        {children}
      </span>
      {revealKey && (
        <Eye className="absolute -right-4 top-1/2 -translate-y-1/2 h-3 w-3 opacity-60" />
      )}
    </span>
  );
});

export default Money;
